import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  LinearProgress,
  styled,
  Typography,
} from "@mui/material";
import React, { memo, useCallback, useEffect, useState } from "react";
import { useAccount } from "../../../context";
import { Template } from "../../../interfaces/all";
import { apiService } from "../../../services/axios";
import { ParameterConfig } from "../../../types/template-config";

const SendButtonContainer = styled(Box)`
  position: sticky;
  bottom: 0;
  background: #fff;
  border-top: 1px solid #e9edef;
  padding: 10px;
  margin-top: auto;
`;

interface SendProgress {
  total: number;
  sent: number;
  failed: number;
  current: number;
  status: "idle" | "sending" | "completed" | "error";
}

interface BulkSenderProps {
  selectedCampaign: string;
  selectedTemplate: Template | null;
  templateParameters: any;
  parameterConfigs: ParameterConfig[];
  accountName?: string;
  isSendButtonEnabled: boolean;
  isSending: boolean;
  onSendProgress: (progress: SendProgress) => void;
  onSendComplete: () => void;
  onError: (error: string) => void;
}

const BulkSender: React.FC<BulkSenderProps> = memo(
  ({
    selectedCampaign,
    selectedTemplate,
    templateParameters,
    parameterConfigs,
    accountName,
    isSendButtonEnabled,
    isSending,
    onSendProgress,
    onSendComplete,
    onError,
  }) => {
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [realtimeProgress, setRealtimeProgress] = useState<any>(null);
    const [isCancelling, setIsCancelling] = useState(false);
    const { socket } = useAccount();

    // Escutar eventos de job assíncrono via socket
    useEffect(() => {
      if (!socket?.current) return;

      const handleBulkJobStarted = (data: any) => {
        setRealtimeProgress({
          jobId: data.jobId,
          status: "processing",
          message: `Processando ${data.templateName}...`,
        });
      };

      const handleBulkJobProgress = (data: any) => {
        setRealtimeProgress({
          jobId: data.jobId,
          status: "processing",
          total: data.total,
          sent: data.sent,
          failed: data.failed,
          processed: data.processed,
          progress: data.progress,
          currentBatch: data.currentBatch,
          totalBatches: data.totalBatches,
        });

        // Atualizar progresso no componente pai
        onSendProgress({
          total: data.total,
          sent: data.sent,
          failed: data.failed,
          current: data.processed,
          status: "sending",
        });
      };

      const handleBulkJobCompleted = (data: any) => {
        setRealtimeProgress({
          jobId: data.jobId,
          status: "completed",
          total: data.result.total,
          sent: data.result.sent,
          failed: data.result.failed,
          processed: data.result.total,
          progress: 100,
          message: "Envio em massa concluído com sucesso!",
        });

        // Atualizar progresso final no componente pai
        onSendProgress({
          total: data.result.total,
          sent: data.result.sent,
          failed: data.result.failed,
          current: data.result.total,
          status: "completed",
        });
      };

      const handleBulkJobFailed = (data: any) => {
        setRealtimeProgress({
          jobId: data.jobId,
          status: "failed",
          error: data.error,
          message: `Erro: ${data.error}`,
        });

        onError(`Job falhou: ${data.error}`);
      };

      const handleBulkJobCancelled = (data: any) => {
        setRealtimeProgress({
          jobId: data.jobId,
          status: "cancelled",
          message: "Job cancelado pelo usuário",
        });
      };

      // Registrar listeners para eventos de job
      socket.current.on("bulkJobStarted", handleBulkJobStarted);
      socket.current.on("bulkTemplateProgress", handleBulkJobProgress);
      socket.current.on("bulkJobCompleted", handleBulkJobCompleted);
      socket.current.on("bulkJobFailed", handleBulkJobFailed);
      socket.current.on("bulkJobCancelled", handleBulkJobCancelled);

      return () => {
        socket.current?.off("bulkJobStarted", handleBulkJobStarted);
        socket.current?.off("bulkTemplateProgress", handleBulkJobProgress);
        socket.current?.off("bulkJobCompleted", handleBulkJobCompleted);
        socket.current?.off("bulkJobFailed", handleBulkJobFailed);
        socket.current?.off("bulkJobCancelled", handleBulkJobCancelled);
      };
    }, [socket, onSendProgress, onError]);

    const handleSendClick = useCallback(() => {
      setShowConfirmation(true);
    }, []);

    const handleCancelSend = useCallback(() => {
      setShowConfirmation(false);
    }, []);

    const handleCancelBulkSend = useCallback(async () => {
      setIsCancelling(true);
      try {
        // Cancelar job via API assíncrona
        if (realtimeProgress?.jobId) {
          await apiService.delete(
            `/bulk-queue/cancel/${realtimeProgress.jobId}`,
          );

          console.log("🚫 [BULK CANCEL] Job cancelado:", {
            jobId: realtimeProgress.jobId,
          });
        }

        // Atualizar progresso para mostrar cancelamento
        onSendProgress({
          total: 0,
          sent: 0,
          failed: 0,
          current: 0,
          status: "error",
        });

        onError("Envio em massa cancelado pelo usuário");
        setShowConfirmation(false);
      } catch (error) {
        console.error("Erro ao cancelar envio:", error);
        onError("Erro ao cancelar envio em massa");
      } finally {
        setIsCancelling(false);
      }
    }, [realtimeProgress, onSendProgress, onError]);

    const handleConfirmSend = useCallback(async () => {
      if (!selectedTemplate || !selectedCampaign) {
        onError("Template ou campanha não selecionados");
        return;
      }

      // Validar parâmetros necessários
      if (
        templateParameters &&
        templateParameters.requiredParametersCount > 0
      ) {
        // Sempre usar modo avançado - configurações personalizadas
        parameterConfigs.forEach((config) => {
          if (config.type === "fixed") {
            // Valor fixo já configurado
          } else {
            // Para modo dinâmico, placeholder será substituído no backend
            switch (config.dynamicField) {
              case "clientName":
              case "clientPhone":
              case "companyName":
              case "attendantName":
              case "scheduledDate":
              default:
              // Placeholder será processado no backend
            }
          }
        });
      }

      setShowConfirmation(false);

      try {
        // Preparar parâmetros finais
        const finalParameters = parameterConfigs.map((config) => {
          if (config.type === "fixed") {
            return config.value;
          } else {
            // Para modo dinâmico, enviar o valor real baseado no campo dinâmico
            switch (config.dynamicField) {
              case "clientName":
                return "CLIENT_NAME_PLACEHOLDER";
              case "clientPhone":
                return "CLIENT_PHONE_PLACEHOLDER";
              case "companyName":
                return "COMPANY_NAME_PLACEHOLDER";
              case "attendantName":
                return "ATTENDANT_NAME_PLACEHOLDER";
              case "scheduledDate":
                return "SCHEDULED_DATE_PLACEHOLDER";
              default:
                return "CLIENT_NAME_PLACEHOLDER";
            }
          }
        });

        // Iniciar progresso
        onSendProgress({
          total: 0, // Será atualizado pela resposta da API
          sent: 0,
          failed: 0,
          current: 0,
          status: "sending",
        });

        // Estimar volume baseado na campanha (assumindo que campanhas grandes têm mais leads)
        // Para volumes muito grandes (1000+), usar timeout de 30 minutos
        const isLargeVolume =
          selectedCampaign.includes("large") ||
          selectedCampaign.includes("massive");

        // Chamar API assíncrona para envio em massa (não bloqueia a aplicação)
        console.log("👤 [BULK SENDER] Account name:", accountName);
        const response = await (isLargeVolume
          ? apiService.postLargeBulk("/bulk-queue/start", {
              campaignId: selectedCampaign,
              templateId: selectedTemplate.id,
              templateName: selectedTemplate.name,
              parameters: finalParameters,
              sentBy: accountName || "Usuário",
            })
          : apiService.postBulk("/bulk-queue/start", {
              campaignId: selectedCampaign,
              templateId: selectedTemplate.id,
              templateName: selectedTemplate.name,
              parameters: finalParameters,
              sentBy: accountName || "Usuário",
            }));

        if (
          response &&
          response.status === "success" &&
          response.data &&
          typeof response.data === "object" &&
          response.data !== null &&
          "jobId" in response.data
        ) {
          const { jobId } = response.data as { jobId: string };

          // Atualizar progresso inicial
          onSendProgress({
            total: 0,
            sent: 0,
            failed: 0,
            current: 0,
            status: "sending",
          });

          // Iniciar monitoramento do job via socket
          setRealtimeProgress({
            jobId,
            status: "processing",
            message: "Envio em massa iniciado. Processamento em background...",
          });

          onSendComplete();
          setShowConfirmation(false);
        } else {
          throw new Error("Erro ao iniciar envio em massa");
        }
      } catch (error) {
        console.error("Erro ao enviar mensagens em massa:", error);
        onError(
          error instanceof Error ? error.message : "Erro ao enviar mensagens",
        );
        onSendProgress({
          total: 0,
          sent: 0,
          failed: 0,
          current: 0,
          status: "error",
        });
      }
    }, [
      selectedTemplate,
      selectedCampaign,
      templateParameters,
      parameterConfigs,
      accountName,
      onSendProgress,
      onSendComplete,
      onError,
    ]);

    return (
      <>
        <SendButtonContainer>
          <Button
            variant="contained"
            fullWidth
            size="large"
            onClick={handleSendClick}
            disabled={!isSendButtonEnabled || isSending}
            sx={{
              backgroundColor: "#00a884",
              "&:hover": {
                backgroundColor: "#008f6f",
              },
              "&:disabled": {
                backgroundColor: "#ccc",
              },
            }}
          >
            {isSending ? "Enviando..." : "Enviar para Todos os Clientes"}
          </Button>

          {isSending && (
            <Box sx={{ mt: 2 }}>
              <LinearProgress />
              <Typography
                variant="caption"
                sx={{ mt: 1, display: "block", textAlign: "center" }}
              >
                Enviando mensagens...
              </Typography>
            </Box>
          )}
        </SendButtonContainer>

        {/* Dialog de Confirmação */}
        <Dialog
          open={showConfirmation}
          onClose={handleCancelSend}
          maxWidth="sm"
          fullWidth
          slotProps={{
            paper: {
              sx: {
                borderRadius: "12px",
                boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
              },
            },
          }}
        >
          <DialogTitle
            sx={{
              backgroundColor: "#f8f9fa",
              borderBottom: "1px solid #e9ecef",
              fontWeight: 600,
              color: "#111b21",
            }}
          >
            Confirmar Envio em Massa
          </DialogTitle>

          <DialogContent sx={{ pt: 3 }}>
            <DialogContentText sx={{ mb: 2, color: "#666" }}>
              Você está prestes a enviar o template{" "}
              <strong>&quot;{selectedTemplate?.name}&quot;</strong> para todos
              os clientes da campanha selecionada.
            </DialogContentText>
            <Box
              sx={{
                backgroundColor: "#f0f7ff",
                border: "1px solid #b3d9ff",
                borderRadius: "8px",
                p: 2,
                mb: 2,
              }}
            >
              <Typography
                variant="body2"
                sx={{ color: "#0066cc", fontWeight: 500 }}
              >
                ⚠️ Esta ação não pode ser desfeita. Certifique-se de que todos
                os parâmetros estão configurados corretamente.
              </Typography>
            </Box>

            {/* Informações dos Parâmetros */}
            {templateParameters &&
              templateParameters.requiredParametersCount > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 600, mb: 1 }}
                  >
                    Parâmetros Configurados:
                  </Typography>
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                  >
                    {parameterConfigs.map((config, index) => (
                      <Box
                        key={index}
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ minWidth: "80px", fontWeight: 500 }}
                        >
                          Parâmetro {index + 1}:
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#666" }}>
                          {config.type === "fixed"
                            ? config.value
                            : `${config.dynamicField} (${config.dynamicField})`}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}

            {/* Progresso em Tempo Real */}
            {realtimeProgress && (
              <Box
                sx={{
                  mt: 3,
                  p: 2,
                  backgroundColor: "#f0f7ff",
                  borderRadius: 1,
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                  📊 Progresso do Envio:
                </Typography>

                {/* Informações do Job */}
                {realtimeProgress.jobId && (
                  <Box
                    sx={{
                      mb: 2,
                      p: 1,
                      backgroundColor: "#e3f2fd",
                      borderRadius: 1,
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      Job ID: {realtimeProgress.jobId}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#666" }}>
                      Status: {realtimeProgress.status}
                    </Typography>
                    {realtimeProgress.message && (
                      <Typography
                        variant="body2"
                        sx={{ color: "#666", fontStyle: "italic" }}
                      >
                        {realtimeProgress.message}
                      </Typography>
                    )}
                  </Box>
                )}

                {/* Barra de Progresso */}
                {realtimeProgress.progress !== undefined && (
                  <Box sx={{ mb: 2 }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 1,
                      }}
                    >
                      <Typography variant="body2">
                        {realtimeProgress.currentBatch &&
                        realtimeProgress.totalBatches
                          ? `Lote ${realtimeProgress.currentBatch} de ${realtimeProgress.totalBatches}`
                          : "Processando..."}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {realtimeProgress.progress}%
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={realtimeProgress.progress}
                      sx={{ height: 8, borderRadius: 4 }}
                    />
                  </Box>
                )}

                {/* Contadores */}
                {(realtimeProgress.total > 0 ||
                  realtimeProgress.sent > 0 ||
                  realtimeProgress.failed > 0) && (
                  <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                    <Chip
                      label={`Total: ${realtimeProgress.total}`}
                      size="small"
                      color="default"
                    />
                    <Chip
                      label={`Enviados: ${realtimeProgress.sent}`}
                      size="small"
                      color="success"
                    />
                    <Chip
                      label={`Falharam: ${realtimeProgress.failed}`}
                      size="small"
                      color="error"
                    />
                    <Chip
                      label={`Processados: ${realtimeProgress.processed}`}
                      size="small"
                      color="info"
                    />
                  </Box>
                )}

                {/* Status Final */}
                {realtimeProgress.status === "completed" && (
                  <Alert severity="success" sx={{ mt: 2 }}>
                    ✅ Envio em massa concluído com sucesso!
                  </Alert>
                )}

                {realtimeProgress.status === "failed" && (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    ❌ Erro: {realtimeProgress.error || "Erro desconhecido"}
                  </Alert>
                )}

                {realtimeProgress.status === "cancelled" && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    🚫 Envio em massa cancelado
                  </Alert>
                )}
              </Box>
            )}
          </DialogContent>

          <DialogActions sx={{ p: 3, gap: 1 }}>
            {isSending ? (
              <>
                <Button
                  onClick={handleCancelBulkSend}
                  variant="outlined"
                  color="error"
                  disabled={isCancelling}
                >
                  {isCancelling ? "Cancelando..." : "Cancelar Envio"}
                </Button>
                <Button
                  disabled
                  variant="contained"
                  sx={{ backgroundColor: "#ccc" }}
                >
                  Enviando...
                </Button>
              </>
            ) : (
              <>
                <Button onClick={handleCancelSend} variant="outlined">
                  Cancelar
                </Button>
                <Button
                  onClick={handleConfirmSend}
                  variant="contained"
                  sx={{ backgroundColor: "#00a884" }}
                >
                  Confirmar Envio
                </Button>
              </>
            )}
          </DialogActions>
        </Dialog>
      </>
    );
  },
);

BulkSender.displayName = "BulkSender";

export default BulkSender;
